import React, {useState, useEffect, useRef} from 'react';
import {Modal, Spinner, ListGroup, Button, Alert, Col, Row, Card, FormControl, InputGroup, Form, Badge} from 'react-bootstrap';
import {useEnhancedTranslation} from "laravel-inertia-backend";
import AddressComponent from "../../Components/AddressComponent.jsx";
import {mdiOpenInNew, mdiRefresh} from "@mdi/js";
import {Icon} from '@mdi/react';
import {usePage} from "@inertiajs/react";
import {
    PiArrowElbowDownRight,
    PiBuilding,
    PiBuildingOffice,
    PiCaretRight,
    PiMagnifyingGlass,
    PiPerson,
    PiUser
} from "react-icons/pi";

/**
 * Kennzeichnung, wie sicher ein vorgeschlagener Kunde zur Bestellung passt.
 * Die Einstufung kommt aus dem CustomerMatcher im Backend.
 */
/**
 * Zeichen je Prüfpunkt: Haken bei Übereinstimmung, Tilde bei teilweiser
 * Übereinstimmung (= der Grund, warum der Kunde überhaupt vorgeschlagen wird),
 * Kreuz bei Abweichung.
 */
const MATCH_ICONS = {
    yes: '✓',
    partial: '≈',
    no: '✕',
};

const MATCH_BADGES = {
    exact: {variant: 'success', label: 'email matches'},
    strong: {variant: 'warning', label: 'probable match'},
    possible: {variant: 'light', label: 'possible match'},
    // "unlikely" bekommt bewusst kein Kennzeichen: der Kunde steht nur wegen
    // eines einzelnen schwachen Merkmals in der Liste (etwa eines ähnlichen
    // Nachnamens). Die Begründung darunter sagt das deutlicher als ein Label.
    unlikely: {variant: 'light', label: 'rather not'},
};

function WeclappCustomerSearchModal({show, onHide, onCustomerSelect, order}) {
    const {t} = useEnhancedTranslation();
    const [customers, setCustomers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [customer, setCustomer] = useState(null); // Aktuell ausgewählter Kontakt
    const [selectedContact, setSelectedContact] = useState(null);
    // Rückfrage vor dem Neuanlegen, wenn es bereits passende Kunden gibt.
    const [duplicateWarning, setDuplicateWarning] = useState(false);
    const {weclappBaseUrl} = usePage().props;
    const searchTermRef = useRef("");

    // Kunden, die laut Bewertung schon zur Bestellung passen. Wird beim
    // Neuanlegen als Dubletten-Warnung ausgewertet.
    const likelyMatches = customers.filter(c => c.match_level === 'exact' || c.match_level === 'strong');

    useEffect(() => {
        if (show) {
            fetchCustomers();
        } else {
            setDuplicateWarning(false);
        }
    }, [show]);

    const fetchCustomers = async (term = searchTermRef.current) => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get('/weclapp/customer/find', {
                params: {order: order.id, search: term},
            });
            setCustomers(response.data);

            if (customer) {
                setCustomer(response.data.find(c => c.id === customer.id));
            }
        } catch (err) {
            setError(t('Failed to fetch customer. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    }

    // Suche verzögert nachziehen, während getippt wird - so muss niemand mehr
    // die Eingabetaste treffen, um überhaupt Vorschläge zu sehen.
    useEffect(() => {
        searchTermRef.current = searchTerm;

        if (!show) {
            return;
        }

        if (searchTerm.length > 0 && searchTerm.length < 2) {
            return;
        }

        const timer = setTimeout(() => fetchCustomers(searchTerm), 400);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const handleCustomerSelect = (selectedCustomer) => setCustomer(selectedCustomer);

    const openWeclappCustomer = (id) => window.open(`${weclappBaseUrl}webapp/view/party/PartyDetail.page?entityId=${id}`, '_blank');

    const refreshWeclappCustomers = () => fetchCustomers();

    /**
     * Klick auf "in Weclapp anlegen". Gibt es bereits passende Kunden, wird
     * zuerst zurückgefragt - so entstehen keine Doppelanlagen aus Versehen.
     */
    const handleCreateClick = () => {
        if (likelyMatches.length > 0 && !duplicateWarning) {
            setDuplicateWarning(true);
            return;
        }

        onCreateWeclappCustomer();
    };

    const onCreateWeclappCustomer = async () => {
        setLoading(true);
        setDuplicateWarning(false);

        try {
            const response = await axios.get('/weclapp/customer/create?order=' + order.id);
            setCustomer(response.data);
            fetchCustomers();
        } catch (err) {
            setError(t('Failed to create customer. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('assign weclapp customer to order')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="border-bottom pb-3">
                    <Card style={{backgroundColor: '#f8f9fa'}}>
                        <Card.Header as={"h6"}>{t('order')}</Card.Header>
                        <Card.Body style={{fontSize: "0.95em"}}>
                            <Row>
                                <Col className={"mb-3"}>
                                    <strong>{order.customer_name}</strong><br/>{order.customer_email}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <b>{t('billing address')}</b>
                                    <AddressComponent address={order.billing_address}/>
                                    {order.vat_id && <div>{t('vat.ID')}: {order.vat_id}</div>}
                                </Col>
                                <Col>
                                    <b>{t('shipping address')}</b>
                                    <AddressComponent address={order.shipping_address}/>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>

                    <div className="d-flex pt-3" style={{height: '400px'}}>
                        {/* Linke Hälfte: Kontaktliste */}
                        <div className="d-flex flex-column w-50 border-end pe-3" style={{textAlign: 'center'}}>
                            {/* Suchfeld */}
                            <InputGroup className="mb-3">
                                <FormControl
                                    type={"search"}
                                    placeholder={t('search customers')}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && searchTerm.length > 0) {
                                            fetchCustomers();
                                        }
                                    }}
                                />
                                <Button variant="secondary" onClick={fetchCustomers} disabled={searchTerm.length === 0}
                                        style={{borderLeft: 'none'}}>
                                    <PiMagnifyingGlass></PiMagnifyingGlass>
                                </Button>
                            </InputGroup>
                            {loading ? (
                                <div className="text-center pt-3">
                                    <Spinner animation="border"/>
                                    <p>Lade Kunden...</p>
                                </div>
                            ) : error ? (
                                <Alert variant="danger" className="text-center mt-3">
                                    {error}
                                </Alert>
                            ) : customers.length === 0 ? (
                                <Alert variant="info" className="text-center">{t('no customers found')}</Alert>
                            ) : (
                                <Card className="overflow-auto flex-auto">
                                    <ListGroup
                                        className={'weclapp-list-group list-group-flush'}
                                        style={{textAlign: 'left'}}
                                    >
                                        {customers.map((item) => (
                                            <ListGroup.Item
                                                as="li"
                                                className="user-select-none d-flex justify-content-between align-items-start"
                                                key={item.id}
                                                action
                                                onClick={() => handleCustomerSelect(item)}
                                                active={customer && customer.id === item.id}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <div className="mx-2 w-100">
                                                    {item.company &&
                                                        <>
                                                            <div className="fw-bold">
                                                                {item.partyType === 'PERSON' &&
                                                                    <PiUser className="me-2"/>}
                                                                {item.partyType === 'ORGANIZATION' &&
                                                                    <PiBuildingOffice className="me-2"/>}
                                                                {item.company}</div>
                                                            {item.lastName && <>({item.firstName} {item.lastName}<br/></>}
                                                            {item.email}
                                                        </>
                                                    }
                                                    {!item.company &&
                                                        <>
                                                            <div className="fw-bold">
                                                                {item.partyType === 'PERSON' &&
                                                                    <PiUser className="me-2"/>}
                                                                {item.partyType === 'ORGANIZATION' &&
                                                                    <PiBuildingOffice className="me-2"/>}
                                                                {item.firstName} {item.lastName}</div>
                                                            {item.email}</>
                                                    }
                                                    {item && item.contacts?.length > 0 && (
                                                        <div className={"customer-contacts pt-1"}>
                                                            {item.contacts.map((contact, index) => (
                                                            <div key={contact.id || index}><PiCaretRight size={'1em'}/>
                                                                <span>{contact.firstName} {contact.lastName}</span>
                                                            </div>
                                                            ))}

                                                        </div>
                                                    )}
                                                    {item.match_checks?.length > 0 && (
                                                        <ul className="weclapp-match-checks">
                                                            {item.match_checks.map((check) => (
                                                                <li key={check.key} className={`is-${check.status}`}>
                                                                    <span className="check-icon" aria-hidden="true">
                                                                        {MATCH_ICONS[check.status] || MATCH_ICONS.no}
                                                                    </span>
                                                                    <span>{check.text}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                                {item.match_level && MATCH_BADGES[item.match_level] && (
                                                    <Badge
                                                        bg={MATCH_BADGES[item.match_level].variant}
                                                        text={MATCH_BADGES[item.match_level].variant === 'light' ? 'dark' : undefined}
                                                        className={`flex-shrink-0 weclapp-match-badge is-${item.match_level}`}
                                                    >
                                                        {t(MATCH_BADGES[item.match_level].label)}
                                                    </Badge>
                                                )}
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                </Card>
                            )}
                            {order.connection_id !== 1 && (
                                <>
                                    {duplicateWarning && (
                                        <Alert variant="warning" className="mt-3 mb-0 text-start small">
                                            <div className="fw-bold">{t('possible duplicate')}</div>
                                            <div>
                                                {likelyMatches.length === 1
                                                    ? t('there is already one matching customer in weclapp.')
                                                    : likelyMatches.length + ' ' + t('customers in weclapp already match this order.')}
                                                {' '}
                                                {t('please check the list before creating a new one.')}
                                            </div>
                                            <div className="d-flex gap-2 mt-2">
                                                <Button size="sm" variant="secondary" onClick={() => setDuplicateWarning(false)}>
                                                    {t('back to list')}
                                                </Button>
                                                <Button size="sm" variant="outline-danger" onClick={onCreateWeclappCustomer}>
                                                    {t('create anyway')}
                                                </Button>
                                            </div>
                                        </Alert>
                                    )}
                                    <Button className={"mt-3"} variant="secondary" onClick={handleCreateClick}>
                                        {t('create in weclapp')}
                                    </Button>
                                </>
                            )}
                        </div>
                        {/* Rechte Hälfte: Kontakt-Details */}
                        <div className="w-50 ps-3 overflow-auto">
                            {customer ? (
                                <>
                                    <div className={'toolbar float-end'}>
                                        <Icon onClick={() => openWeclappCustomer(customer.id)} path={mdiOpenInNew}
                                              size={1}/>
                                        <Icon onClick={refreshWeclappCustomers} path={mdiRefresh} size={1}/>
                                    </div>
                                    <div>
                                        {customer.company ? <h5>{customer.company}</h5> :
                                            <h5>{customer.firstName} {customer.lastName}</h5>}
                                        <p><strong>Kundennummer:</strong> {customer.customerNumber}</p>
                                        <p><strong>Email:</strong> {customer.email}</p>
                                        <p><strong>Telefon:</strong> {customer.phone}</p>
                                        <p><strong>Website:</strong> {customer.website}</p>
                                        {customer.addresses?.length > 0 && (
                                            <>
                                                <h6>Adresse</h6>
                                                <p>
                                                    {customer.addresses[0].street1}
                                                    <br/>
                                                    {customer.addresses[0].street2 && (
                                                        <>
                                                            {customer.addresses[0].street2}
                                                            <br/>
                                                        </>
                                                    )}
                                                    {customer.addresses[0].zipcode} {customer.addresses[0].city}
                                                    <br/>
                                                    {customer.addresses[0].countryCode}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    {customer.contacts && customer.contacts.length > 0 && (
                                        <>
                                            <h5>{t('contact')}</h5>
                                            <Form.Select
                                                value={selectedContact ? selectedContact.id : ''}
                                                onChange={(e) => {
                                                    const contact = customer.contacts.find(c => c.id === e.target.value);
                                                    setSelectedContact(contact);
                                                }}
                                            >
                                                <option value="">{t('no contact')}</option>
                                                {customer.contacts.map(contact => (
                                                    <option key={contact.id} value={contact.id}>
                                                        {contact.firstName} {contact.lastName}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {selectedContact && (
                                                <div className="mt-3">
                                                    <p><strong>Email:</strong> {selectedContact.email}</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="text-center text-muted">
                                    <p>{t('select customer to show details')}</p>
                                </div>
                            )}
                        </div>
                    </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {t('close')}
                </Button>
                <Button
                    variant="weclapp"
                    onClick={() => {
                        if (customer) {
                            onCustomerSelect(customer, selectedContact);
                            onHide();
                            onHide();
                        }
                    }}
                    disabled={!customer}
                >
                    {t('assign customer')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default WeclappCustomerSearchModal;
